import { TypeAnimation } from "react-type-animation";
import { motion } from "framer-motion";
import { useState } from "react";
import { BsMoonFill, BsSunFill } from "react-icons/bs";
import { SiExpress, SiMongodb, SiNodedotjs, SiReact } from "react-icons/si";
import { RiTailwindCssFill } from "react-icons/ri";
import { FiGithub } from "react-icons/fi";
import { GrMysql } from "react-icons/gr";
import FeaturedProject from "./FeaturedProject";

const Intro = () => {
  const [lightMode, setLightMode] = useState(false);

  const techStack = [
    { name: "React", icon: <SiReact /> },
    { name: "Node.js", icon: <SiNodedotjs /> },
    { name: "Express", icon: <SiExpress /> },
    { name: "MongoDB", icon: <SiMongodb /> },
    { name: "Tailwind", icon: <RiTailwindCssFill /> },
    { name: "MySQL", icon: <GrMysql /> },
  ];

  return (
    <div
      className={`
        w-full min-h-screen flex flex-col items-center justify-start
        pt-14 sm:pt-16 md:pt-20
        px-3 sm:px-4 md:px-6
        gap-6 sm:gap-8 md:gap-10
        relative overflow-hidden transition-colors duration-300
        ${lightMode ? "bg-white text-black" : "bg-black text-white"}
      `}>
      {/* Top Icons */}
      <div className="flex flex-row gap-2 sm:gap-3 absolute top-3 sm:top-4 left-3 sm:left-4 z-20">
        <a
          href="https://github.com/PritamMaharjan86"
          target="_blank"
          rel="noopener noreferrer"
          className={`text-lg p-2 rounded-full transition-all active:scale-75 ${
            lightMode ?
              "text-gray-800 hover:bg-gray-200"
            : "text-gray-300 hover:bg-gray-800"
          }`}>
          <FiGithub />
        </a>
      </div>

      {/* Theme Toggle */}
      <button
        onClick={() => setLightMode((prev) => !prev)}
        className={`
          absolute top-3 sm:top-4 right-3 sm:right-4
          p-2 rounded-full transition-colors duration-300 active:scale-75
          ${lightMode ? "text-gray-800 hover:bg-gray-200" : "text-gray-300 hover:bg-gray-800"}
        `}>
        {lightMode ?
          <BsMoonFill size={16} />
        : <BsSunFill size={16} />}
      </button>

      {/* Heading */}
      <h1
        className={`
          z-10 font-title font-bold tracking-wider text-center
          mt-16 sm:mt-20 md:mt-24
          text-xl sm:text-3xl md:text-5xl lg:text-6xl
          ${lightMode ? "text-gray-500" : "text-gray-300"}
        `}>
        <TypeAnimation
          sequence={["Hello World!", 5000, "I am Pritam", 5000]}
          wrapper="span"
          cursor
          repeat={Infinity}
        />
      </h1>

      {/* Description */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className={`
          mt-4 sm:mt-8 md:mt-10
          text-xs sm:text-sm md:text-base
          px-2 sm:px-0
          font-terminal tracking-widest text-center
          max-w-2xl sm:max-w-3xl leading-relaxed z-10
          ${lightMode ? "text-gray-600" : "text-gray-400"}
        `}>
        Software engineer in progress, focused on crafting simple, effective,
        and user-friendly web experiences.
      </motion.p>

      {/* Tech Stack */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="
          cursor-pointer flex flex-wrap
          items-center justify-center
          gap-2 sm:gap-3 md:gap-4
          z-10 mt-6 sm:mt-10
          px-2
        ">
        {techStack.map((tech) => (
          <span
            key={tech.name}
            className={`
              flex items-center gap-2
              px-2 sm:px-3 py-1 rounded-lg
              text-[10px] sm:text-xs
              font-terminal tracking-widest
              border transition-all duration-300 ease-out
              ${
                lightMode ?
                  "border-gray-400 text-gray-700 hover:bg-gray-200"
                : "border-gray-600 text-gray-400 hover:bg-white/10"
              }
              hover:-translate-y-0.5
            `}>
            <span className="text-sm">{tech.icon}</span>
            <span>{tech.name}</span>
          </span>
        ))}
      </motion.div>

      {/* Featured Project */}
      <div className="flex flex-col items-center mt-8 sm:mt-12 z-10">
        <span
          className={`text-xs sm:text-sm md:text-base tracking-wider font-terminal mb-4 sm:mb-5 ${
            lightMode ? "text-gray-600" : "text-gray-400"
          }`}>
          My Featured Project
        </span>

        <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
          <FeaturedProject
            title="Smart Tokari"
            description="Inventory and sales management system with product tracking, sales processing, GST and discount calculations, and receipt generation."
            tech={["React", "Node.js", "Express", "MySQL", "Tailwind CSS"]}
            link="https://smarttokari.vercel.app/"
            lightMode={lightMode}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto mb-4 z-10">
        <p className="text-[10px] sm:text-xs text-center font-terminal tracking-widest text-gray-400">
          Created by <span className="font-bold">Pritam</span>.
        </p>
      </div>
    </div>
  );
};

export default Intro;
